import {
  Alert,
  Button,
  Heading,
  Menu,
  MenuItem,
  MenuToggleButton,
  Tag,
  Text,
} from "@growtherapy/sprout-ui";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { unreadMessagesCountFamily } from "../../session/state";
import { VisitorPresence, VisitorPresenceMap } from "../../types";
import { useOpenClientInformationCallback } from "../client-information/useClientInformationDrawerCallbacks";
import {
  inSessionPatientInformationAtom,
  joiningVisitorIdFamily,
  newSessionConfirmationDataAtom,
  providerShortIdAtom,
  waitingRoomConversationSidsByVisitorIdAtom,
  waitingRoomConversationSidsByVisitorIdFamily,
  waitingVisitorsAtom,
} from "../state";
import { ClientActions } from "./ClientActions";
import { ClientActionsMenu } from "./ClientActionsMenu";
import { openWaitingRoomConversation } from "./openWaitingRoomConversation";
import { useAdmitClientCallback } from "./useAdmitClientCallback";

import { useFeatureGate } from "@statsig/react-bindings";
import {
  AppointmentTime,
  type AppointmentTimeProps,
} from "../../components/AppointmentTime";
import { TrackingEvents, sendLoggingEvents } from "../../events";
import { useGetTelehealthSessionInfo } from "../../hooks";
import { delayedEntryGate } from "../../statsig/gates";
import { participantCountAtom, VideoPlatform } from "../../video-sdk";
import { useOpenConversationCallback } from "../chat/useOpenConversationCallback";
import { ClientMoodWrapper as ClientMood } from "../client-information/ClientMood";
import { hasNewFormsAtomFamily } from "../client-information/state";
import { AutomaticallyTransferringTextWrapper as AutomaticallyTransferringText } from "../delayed-entry/AutomaticallyTransferringText";
import { useVideoPlatform } from "../../hooks/useVideoPlatform";
import { IncompatibleApplicationAdmitButton } from "./IncompatibleApplicationAdmitButton";
import { useIsVideoPlatformDowngraded } from "../../hooks/useIsVideoPlatformDowngraded";
import { logger } from "../../datadog";

type ClientQueueActionsProps = {
  appointment?: AppointmentTimeProps["appointment"];
  clientNameAnnotation?: ReactNode;
  currentSessionPatientShortId: VisitorPresence["patientShortId"] | null;
  inSession?: boolean;
  isAppointmentLoading?: AppointmentTimeProps["isLoading"];
  isConversationLoading?: boolean;
  isClientInformationLoading?: boolean;
  loading?: boolean;
  onChat?: () => void;
  onOpenClientInformationDrawer: () => void;
  onAdmit: () => void;
  onShowStartNewSessionConfirmation: () => void;
  unreadMessagesCount?: number;
  visitorName: string;
  visitorUuid: string;
  visitorVideoPlatform?: VideoPlatform | null;
  providerVideoPlatform?: VideoPlatform | null;
  patientShortId: string;
  hideAdmitButton: boolean;
};

type ClientQueueActionButtonsProps = Pick<
  ClientQueueActionsProps,
  | "currentSessionPatientShortId"
  | "inSession"
  | "loading"
  | "onAdmit"
  | "onShowStartNewSessionConfirmation"
  | "patientShortId"
  | "visitorName"
  | "visitorVideoPlatform"
  | "providerVideoPlatform"
>;
function ClientQueueActionButtons({
  currentSessionPatientShortId,
  inSession,
  loading,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  visitorName,
  visitorVideoPlatform,
  providerVideoPlatform,
}: ClientQueueActionButtonsProps) {
  const isRequestForNewSession =
    !!currentSessionPatientShortId &&
    currentSessionPatientShortId !== patientShortId;

  if (visitorVideoPlatform !== providerVideoPlatform) {
    return <IncompatibleApplicationAdmitButton />;
  }

  if (isRequestForNewSession) {
    return (
      <Button
        data-testid={`client-actions.admit:${visitorName}.end-and-start-new-session`}
        loading={loading}
        onClick={onShowStartNewSessionConfirmation}
      >
        Admit
      </Button>
    );
  }

  if (inSession) {
    return (
      <Menu
        placement="bottom end"
        buttonClassName="py-3 h-12"
        toggleButton={
          <MenuToggleButton
            data-testid={`client-actions.menu:${visitorName}`}
            use="primary"
            loading={loading}
          >
            Admit
          </MenuToggleButton>
        }
      >
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.admit-to-current-session`}
          onClick={() => {
            onAdmit();
            sendLoggingEvents(TrackingEvents.PROVIDER_ADMIT_CLIENT);
          }}
        >
          Admit to current session
        </MenuItem>
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.end-and-start-new-session`}
          onClick={() => onShowStartNewSessionConfirmation()}
        >
          End and start new session
        </MenuItem>
      </Menu>
    );
  }

  return (
    <Button
      data-testid={`client-actions.admit:${visitorName}`}
      loading={loading}
      onClick={onAdmit}
    >
      Admit
    </Button>
  );
}

export function ClientQueueActions({
  appointment,
  clientNameAnnotation: providedClientNameAnnotation,
  currentSessionPatientShortId,
  inSession,
  isAppointmentLoading,
  isConversationLoading,
  loading,
  onChat,
  onOpenClientInformationDrawer,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  unreadMessagesCount,
  visitorName,
  visitorUuid,
  hideAdmitButton,
  visitorVideoPlatform,
  providerVideoPlatform,
}: ClientQueueActionsProps) {
  const isRequestForNewSession =
    !!currentSessionPatientShortId &&
    currentSessionPatientShortId !== patientShortId;

  const clientNameAnnotation = (
    <>
      {isRequestForNewSession ? (
        <AppointmentTime
          appointment={appointment}
          isLoading={isAppointmentLoading}
          variant="xs"
        />
      ) : (
        <AutomaticallyTransferringText visitorId={visitorUuid} variant="xs" />
      )}

      {providedClientNameAnnotation}
    </>
  );

  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));

  return (
    <ClientActions
      clientName={visitorName}
      clientNameAnnotation={clientNameAnnotation}
      actions={
        <div className="flex gap-2 shrink-0">
          {!hideAdmitButton && (
            <ClientQueueActionButtons
              currentSessionPatientShortId={currentSessionPatientShortId}
              inSession={inSession}
              loading={loading}
              onAdmit={onAdmit}
              onShowStartNewSessionConfirmation={
                onShowStartNewSessionConfirmation
              }
              patientShortId={patientShortId}
              visitorName={visitorName}
              visitorVideoPlatform={visitorVideoPlatform}
              providerVideoPlatform={providerVideoPlatform}
            />
          )}
          {/* TODO: JRNY-1299 */}
          {/* <Button use="destructive">Deny</Button> */}
          {onChat && (
            <ClientActionsMenu
              visitorName={visitorName}
              loading={loading}
              isConversationLoading={isConversationLoading}
              patientShortId={patientShortId}
              unreadMessagesCount={unreadMessagesCount}
              onChat={onChat}
              onOpenClientInformationDrawer={onOpenClientInformationDrawer}
              hasNewClientInfo={hasNewForms}
            />
          )}
        </div>
      }
    />
  );
}

export function ClientQueueActionsWrapper({
  visitorName,
  visitorUuid,
  patientShortId,
  visitorVideoPlatform,
  providerVideoPlatform,
}: Pick<ClientQueueActionsProps, "visitorName" | "patientShortId"> & {
  visitorUuid: string;
  visitorVideoPlatform?: VideoPlatform | null;
  providerVideoPlatform?: VideoPlatform | null;
}) {
  const inSession = !!useAtomValue(participantCountAtom);
  const [admitClient] = useAdmitClientCallback();
  const setNewSessionConfirmationData = useSetAtom(
    newSessionConfirmationDataAtom,
  );

  const waitingRoomConversationSid = useAtomValue(
    waitingRoomConversationSidsByVisitorIdFamily(visitorUuid),
  );
  const { patientShortId: currentSessionPatientShortId = null } =
    useAtomValue(inSessionPatientInformationAtom) ?? {};
  const getWaitingRoomConversationSids = useAtomCallback(
    useCallback((get) => get(waitingRoomConversationSidsByVisitorIdAtom), []),
  );

  const isWaitingForClientToJoin = useAtomValue(
    joiningVisitorIdFamily(visitorUuid),
  );
  const { value: isDelayedEntry } = useFeatureGate(delayedEntryGate);
  const loading = isDelayedEntry ? false : isWaitingForClientToJoin;
  const openConversation = useOpenConversationCallback();

  const openClientInformation = useOpenClientInformationCallback();

  const openClientInformationSection = useCallback(() => {
    openClientInformation({ visitorUuid, patientShortId });
  }, [openClientInformation, visitorUuid, patientShortId]);

  const unreadMessagesCount = useAtomValue(
    unreadMessagesCountFamily(waitingRoomConversationSid),
  );

  const providerShortId = useAtomValue(providerShortIdAtom);
  const { data, loading: isAppointmentLoading } = useGetTelehealthSessionInfo(
    providerShortId,
    patientShortId,
  );
  const appointment = data?.telehealthSessionInfo?.appointment;

  const clientNameAnnotation = (
    <ClientMood variant="xs" visitorUuid={visitorUuid} />
  );

  return (
    <ClientQueueActions
      {...{
        appointment,
        clientNameAnnotation,
        currentSessionPatientShortId,
        inSession,
        isAppointmentLoading,
        isConversationLoading: !waitingRoomConversationSid,
        loading,
        onOpenClientInformationDrawer: openClientInformationSection,
        onChat: () => {
          openWaitingRoomConversation(
            visitorUuid,
            getWaitingRoomConversationSids,
            openConversation,
          );
        },
        onAdmit: () => {
          admitClient(visitorUuid, visitorName, patientShortId);
        },
        onShowStartNewSessionConfirmation: () => {
          setNewSessionConfirmationData({
            visitorName,
            visitorUuid,
            patientShortId,
          });
        },
        unreadMessagesCount,
        visitorName,
        visitorUuid,
        patientShortId,
        hideAdmitButton: isDelayedEntry && isWaitingForClientToJoin,
        visitorVideoPlatform,
        providerVideoPlatform,
      }}
    />
  );
}

type ClientQueueProps = {
  clients: VisitorPresenceMap;
  clientActionsComponent: typeof ClientQueueActionsWrapper;
  onAdmit?: (uuid: string, name: string) => void;
  loading?: boolean;
  providerVideoPlatform?: VideoPlatform | null;
  onVideoPlatformReinitialization?: () => void;
};

export function ClientQueue({
  clients,
  clientActionsComponent: ClientActionsComponent,
  providerVideoPlatform,
  onVideoPlatformReinitialization,
}: ClientQueueProps) {
  const hasWaitingClients = Object.keys(clients).length;
  const numClientsInQueue = Object.keys(clients).length ?? 0;
  const hasApplicationCompatibilityIssue = Object.values(clients).some(
    (client) =>
      (client.videoPlatform || VideoPlatform.Twilio) !==
      (providerVideoPlatform || VideoPlatform.Twilio),
  );
  const canSupportAllClients =
    new Set(Object.values(clients).map((client) => client.videoPlatform))
      .size === 1;

  return (
    <>
      <div className="flex items-center gap-2">
        <Heading as="h3" variant="md">
          Client queue
        </Heading>
        <Tag
          aria-label={`${numClientsInQueue} waiting`}
          use="mauve"
          data-testid="client-queue.count"
        >
          {numClientsInQueue}
        </Tag>
      </div>
      {hasApplicationCompatibilityIssue && (
        <Alert
          heading="Application compatibility issue"
          use="error"
          headingAs="h3"
          actions={
            <div className="flex flex-col">
              <Button use="secondary" onClick={onVideoPlatformReinitialization}>
                Reinitialize application
              </Button>
              <Text variant="xs" className="italic mt-2">
                Clicking this button will end any current session.
              </Text>
            </div>
          }
        >
          {!canSupportAllClients && (
            <Text>
              Clients have incompatible application versions and cannot be
              admitted to the same session. If the desired client has their
              Admit button enabled, you may admit them now. If you prefer to
              admit the other client(s), please click to reinitialize the
              application.
            </Text>
          )}
          {canSupportAllClients && (
            <Text>
              {/* messaging accounts for 1) when the provider has downgraded for
            a mobile app client, and 2) if statsig has updated */}
              One or more clients have an application compatibility issue.
              Please click the button to reinitialize the application. Clients
              may need to refresh their application to join the session.
            </Text>
          )}
        </Alert>
      )}
      {hasWaitingClients ? (
        Object.entries(clients).map(
          ([
            visitorUuid,
            {
              name: visitorName = "",
              patientShortId,
              videoPlatform = VideoPlatform.Twilio,
            },
          ]) => (
            <ClientActionsComponent
              key={visitorUuid}
              {...{
                visitorName,
                visitorUuid,
                patientShortId,
                visitorVideoPlatform: videoPlatform,
                providerVideoPlatform,
              }}
            />
          ),
        )
      ) : (
        <Text>Clients who join your waiting room will appear here.</Text>
      )}
    </>
  );
}

export function ClientQueueWrapper() {
  const waitingClients = useAtomValue(waitingVisitorsAtom);
  const [providerVideoPlatform] = useVideoPlatform();

  // TODO: move to a hook that can be called when clients join
  const [, setIsVideoPlatformDowngraded] = useIsVideoPlatformDowngraded();

  const hasApplicationCompatibilityIssue = Object.values(waitingClients).some(
    (client) =>
      (client.videoPlatform || VideoPlatform.Twilio) !==
      (providerVideoPlatform || VideoPlatform.Twilio),
  );

  const handleVideoPlatformReinitialization = useCallback(() => {
    setIsVideoPlatformDowngraded(
      (isCurrentlyDowngraded) => !isCurrentlyDowngraded,
    );

    window.location.reload();
  }, [setIsVideoPlatformDowngraded]);

  const hasLoggedRef = useRef(false);
  useEffect(() => {
    if (!hasApplicationCompatibilityIssue || hasLoggedRef.current) return;

    hasLoggedRef.current = true;
    logger.warn("Application compatibility issue detected", {
      clientVideoPlatforms: Object.values(waitingClients).map(
        (client) => client.videoPlatform,
      ),
      providerVideoPlatform,
    });
  }, [hasApplicationCompatibilityIssue, waitingClients, providerVideoPlatform]);

  return (
    <ClientQueue
      clients={waitingClients}
      clientActionsComponent={ClientQueueActionsWrapper}
      providerVideoPlatform={providerVideoPlatform}
      onVideoPlatformReinitialization={handleVideoPlatformReinitialization}
    />
  );
}
